import {
	Maybe,
	Regulation,
	RegulatoryDocument,
	Requirement,
	VexCluster,
} from 'types';
import {TFunction} from 'react-i18next';
import {
	IArrayRoles,
	renderHeaderXL,
	createZIPXL,
	addHTMLImagesAsLinks,
	renderRowItemAsLinkXL,
	renderStringArrayXL,
	renderArrayXL,
	renderDateXL,
	renderHTMLStringXL,
	mergeColumnsExcel,
	getColumnAlphabetExcel,
} from './ExcelExportCommon';
import Excel from 'exceljs';
import {InfinityListColumn} from '../InfinityList';
import {XLColumn} from './ExcelExportCommon';

interface IRequirementPage {
	(
		entityListItems: any[],
		columns: InfinityListColumn[],
		arrRoles: IArrayRoles,
		myVexClusters: VexCluster[],
		t: TFunction,
	): Promise<void>;
}

export const getColumnsXLRender = (t: TFunction) => [
	{
		key: 'attachments',
		xlName: ' ',
		xlHideFilter: true,
		xlWidth: 5,
	},
	{
		key: 'name',
		xlRender: (iRequirement: Requirement) => iRequirement.name,
		xlWidth: 60,
	},
	{
		key: 'requirementId',
		xlRender: (iRequirement: Requirement) => ({
			text: iRequirement.requirementId,
			hyperlink: window.location.origin + '/requirements/' + iRequirement.id,
		}),
		xlFontStyle: {
			underline: true,
			bold: false,
			color: {argb: '00B0F0'},
		},
	},
	{
		key: 'version',
		xlRender: (iRequirement: Requirement) => iRequirement.version ?? 'V1.0',
	},
	{
		key: 'regulations',
		xlRender: (iRequirement: Requirement) =>
			renderStringArrayXL(renderRegulationNumberXL(iRequirement.regulations)),
	},
	{
		key: 'regulatoryDocuments',
		xlRender: (iRequirement: Requirement) =>
			renderStringArrayXL(
				renderRegulationVersionXL(iRequirement.regulatoryDocuments),
			),
	},
	{
		key: 'markets',
		xlRender: (iRequirement: Requirement) =>
			renderArrayXL(iRequirement.markets),
	},
	{
		key: 'vexClusters',
		xlRender: (iRequirement: Requirement) =>
			renderArrayXL(iRequirement.vexClusters),
	},
	{
		key: 'dateEffective',
		xlRender: (iRequirement: Requirement) =>
			renderDateXL(iRequirement.dateEffective),
	},
	{
		key: 'dateExpiration',
		xlRender: (iRequirement: Requirement) =>
			renderDateXL(iRequirement.dateExpiration),
	},
	{
		key: 'status',
		xlRender: (iRequirement: Requirement) =>
			t(iRequirement.status ?? '', {
				ns: 'common/enums',
				keyPrefix: 'RequirementStatus',
			}),
	},
	{
		key: 'definition',
		xlRender: (iRequirement: Requirement) =>
			renderHTMLStringXL('', iRequirement.definition),
		xlWidth: 100,
	},
	{
		key: 'systemLevels',
		xlRender: (iRequirement: Requirement) =>
			renderArrayXL(iRequirement.systemLevels),
	},
	{
		key: 'keywords',
		xlRender: (iRequirement: Requirement) =>
			renderArrayXL(iRequirement.keywords),
	},
	{
		key: 'tags',
		xlRender: (iRequirement: Requirement) => renderArrayXL(iRequirement.tags),
	},
	{
		key: 'createdBy',
		xlRender: (iRequirement: Requirement) => iRequirement.createdBy.name,
	},
	{
		key: 'modifiedAt',
		xlRender: (iRequirement: Requirement) =>
			renderDateXL(iRequirement.modifiedAt),
	},
	{
		key: 'category',
		xlRender: (iRequirement: Requirement) =>
			t(iRequirement.category, {
				ns: 'common/enums',
				keyPrefix: 'RequirementCategory',
			}),
	},
	{
		key: 'FAQ',
		xlRender: (iRequirement: Requirement) => ({
			text: `${(iRequirement as any).question?.count}`,
			hyperlink: window.location.origin + '/requirements/' + iRequirement.id,
		}),
	},
];

export const handleClickRequirements: IRequirementPage = async (
	entityListItems,
	columns,
	arrRoles,
	myVexClusters,
	t,
) => {
	let filtRequirement = entityListItems as Requirement[];

	if (arrRoles.isVex) {
		filtRequirement = filtRequirement.filter(x =>
			x.vexClusters.some(vc => myVexClusters.some(mvc => mvc.id === vc.id)),
		);
	}

	const arrImagesUri: string[] = [];
	const arrImagesName: string[] = [];
	const arrTables: string[] = [];

	const workbook = new Excel.Workbook();
	const sheet = workbook.addWorksheet(
		'TRACE_' +
			t('RequirementsHeader', {
				ns: 'features/requirements',
			}),
	);

	const columnsXLRender = getColumnsXLRender(t);

	const columnsXL: XLColumn[] = mergeColumnsExcel(columns, columnsXLRender);

	const arrColWidth = columnsXL.map(x => x.xlWidth ?? 25);
	const strPageName = '';

	renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

	let intRowCount = 4;
	let row = sheet.getRow(intRowCount);

	row.font = {
		bold: true,
		name: 'Porsche Next TT',
		size: 10,
		color: {argb: 'FFFFFFFF'},
	};

	sheet.addTable({
		name: 'tableRequirements',
		ref: 'B' + String(intRowCount),
		style: {
			showRowStripes: true,
		},
		columns: columnsXL,
		rows: [],
	});

	const table = sheet.getTable('tableRequirements');

	intRowCount++;
	const tempDivElement = document.createElement('div');

	if (filtRequirement.length === 0) {
		table.addRow(new Array(columnsXL.length).fill(''));
		intRowCount++;
	}

	for (let ii = 0; ii < filtRequirement.length; ii++) {
		const iRequirement = filtRequirement[ii];

		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};

		columnsXL.forEach((col, index) => {
			const colAlpha = getColumnAlphabetExcel(index + 1);
			if (col?.xlFontStyle) {
				row.getCell(colAlpha).style.font = col.xlFontStyle;
			}
		});

		const renderedXLRow = columnsXL.map(x => {
			const xlRenderFnDef = () => '';
			const xlRenderFn = x.xlRender ?? xlRenderFnDef;
			return xlRenderFn(iRequirement);
		});

		table.addRow(renderedXLRow);

		intRowCount++;

		tempDivElement.innerHTML = iRequirement.definition
			? '<div>' + iRequirement.definition + '</div>'
			: '<div></div>';

		const indexDefintion = columnsXL.findIndex(x => x.key == 'definition') + 1;
		intRowCount = addHTMLImagesAsLinks(
			intRowCount,
			iRequirement.requirementId as string,
			arrImagesName,
			arrImagesUri,
			getColumnAlphabetExcel(indexDefintion + 1),
			indexDefintion,
			tempDivElement,
			sheet,
			table,
			t,
		);

		const arrAttach = iRequirement.attachments ?? [];
		arrAttach.forEach(iAttach => {
			intRowCount = renderRowItemAsLinkXL(
				intRowCount,
				iAttach.file?.fileName ?? iAttach.attachmentId,
				iAttach.file?.uri ?? '',
				'C',
				1,
				sheet,
				table,
				t,
			);
		});
	}

	table.commit();

	const strZipName = t('SideNavigation.Requirements', {
		ns: 'appShell/sidenavigation',
	});

	await createZIPXL(
		workbook,
		strZipName,
		t,
		arrTables,
		arrImagesUri,
		arrImagesName,
		[],
	);
};

export const renderRegulationNumberXL = (
	regulations?: Maybe<Regulation[]> | undefined,
) => {
	return (regulations ?? []).map(reg => reg.regulationNumber);
};

export const renderRegulationVersionXL = (
	regulatoryDocuments?: Maybe<RegulatoryDocument[]> | undefined,
) => {
	return (regulatoryDocuments ?? []).map(regdoc => `${regdoc.name ?? ''}`);
};
